import { generatePath } from 'react-router-dom';
import { baseQueryWithAuthRefresh } from '@qapline/api';
import i18n from '@qapline/translations/i18next';
import { createApi } from '@reduxjs/toolkit/query/react';
import { config } from 'config';

import { GetOneDispatcherResponseSchema, getOneDispatcherResponseSchema } from '../types';
import { TCreateDispatcherRequestBody, TUpdateDispatcherRequestBody } from '../types/api';

export const dispatchersApi = createApi({
    reducerPath: 'dispatchersApi',
    baseQuery: baseQueryWithAuthRefresh,
    tagTypes: ['DispatcherItem'],
    endpoints: (builder) => ({
        getOneDispatcher: builder.query<GetOneDispatcherResponseSchema, { id: number }>({
            providesTags: (_result, _error, arg) => [{ type: 'DispatcherItem', id: arg.id }],
            query: ({ id }) => ({
                url: generatePath(config.api.endpoints.dispatcher.get, { dispatcherId: id.toString() }),
                method: 'GET',
            }),
            transformResponse: (res: any) => {
                const validatedSchema = getOneDispatcherResponseSchema(i18n.t).safeParse(res);
                if (!validatedSchema.success)
                    console.warn('Validation failed in getOneDispatcher', validatedSchema.error);
                return res;
            },
        }),
        postNewDispatcher: builder.mutation<GetOneDispatcherResponseSchema, { data: TCreateDispatcherRequestBody }>({
            query: ({ data }) => ({
                url: config.api.endpoints.dispatcher.create,
                method: 'POST',
                body: data,
            }),
        }),
        putUpdatedDispatcher: builder.mutation<
            GetOneDispatcherResponseSchema,
            { data: TUpdateDispatcherRequestBody; id: number }
        >({
            query: ({ data, id }) => ({
                url: generatePath(config.api.endpoints.dispatcher.update, { dispatcherId: id.toString() }),
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_, error, arg) => (error ? [] : [{ type: 'DispatcherItem', id: arg.id }]),
        }),
        deleteDispatcher: builder.mutation<void, number>({
            query: (dispatcherId) => ({
                url: generatePath(config.api.endpoints.dispatcher.delete, { dispatcherId: dispatcherId.toString() }),
                method: 'DELETE',
            }),
            invalidatesTags: (_, error) => (error ? [] : [{ type: 'DispatcherItem' as const }]),
        }),
        getCheckDispatcherMail: builder.query<{ available: boolean }, { email: string }>({
            query: (data) => ({
                url: config.api.endpoints.checkDispatcherMail,
                method: 'GET',
                params: data,
            }),
        }),
        getCheckOnboardingToken: builder.query<{ valid: boolean }, null>({
            query: () => ({
                url: config.api.endpoints.checkOnboardingToken,
                method: 'GET',
            }),
        }),
        postDispatcherEmail: builder.query<
            any,
            {
                dispatcherId: number;
                subject: string;
                body: string;
            }
        >({
            query: (data) => ({
                url: config.api.endpoints.dispatcherEmail,
                method: 'POST',
                body: data,
            }),
        }),
    }),
});

export const {
    useGetOneDispatcherQuery,
    usePostNewDispatcherMutation,
    usePutUpdatedDispatcherMutation,
    useDeleteDispatcherMutation,
    useLazyGetCheckDispatcherMailQuery,
    useGetCheckOnboardingTokenQuery,
    useLazyPostDispatcherEmailQuery,
} = dispatchersApi;
